// @ts-nocheck
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Users,
    Calendar,
    MessageSquare,
    Globe,
    Star,
    Scale
} from "lucide-react";

// Classic Legal Theme: Burgundy / Wine + Antique Gold + Ivory
const colors = {
    bgTop: "#3b1929",       // Lighter burgundy/wine
    bgBottom: "#2a1220",    // Medium-dark burgundy
    primary: "#c9a84c",     // Antique Gold
    primaryHover: "#b8943f",
    accent: "#8b2252",      // Deep burgundy/wine
    accentLight: "#a83260", // Lighter burgundy
    cardBg: "rgba(139, 34, 82, 0.12)",
    cardBorder: "rgba(201, 168, 76, 0.2)",
    text: "#f5f0e8",        // Warm ivory
    textMuted: "#c4b8a8",   // Muted warm tone
    inputBg: "rgba(255, 255, 255, 0.08)",
};

// SVG Scales of Justice - decorative background element
function ScalesOfJustice() {
    return (
        <svg
            viewBox="0 0 400 500"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[600px] opacity-[0.04] pointer-events-none select-none"
        >
            {/* Central pillar */}
            <rect x="195" y="80" width="10" height="350" fill="currentColor" rx="3" />
            {/* Base */}
            <rect x="140" y="420" width="120" height="12" rx="6" fill="currentColor" />
            <rect x="170" y="410" width="60" height="15" rx="4" fill="currentColor" />
            {/* Top ornament */}
            <circle cx="200" cy="70" r="18" fill="currentColor" />
            <circle cx="200" cy="70" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
            {/* Horizontal beam */}
            <rect x="60" y="95" width="280" height="8" rx="4" fill="currentColor" />
            {/* Left chain */}
            <rect x="68" y="103" width="4" height="80" fill="currentColor" />
            {/* Right chain */}
            <rect x="328" y="103" width="4" height="80" fill="currentColor" />
            {/* Left pan */}
            <ellipse cx="70" cy="195" rx="55" ry="12" fill="currentColor" />
            <path d="M15 195 Q15 230 70 230 Q125 230 125 195" fill="currentColor" opacity="0.6" />
            {/* Right pan */}
            <ellipse cx="330" cy="195" rx="55" ry="12" fill="currentColor" />
            <path d="M275 195 Q275 230 330 230 Q385 230 385 195" fill="currentColor" opacity="0.6" />
            {/* Left chain connections */}
            <line x1="30" y1="183" x2="70" y2="103" stroke="currentColor" strokeWidth="3" />
            <line x1="110" y1="183" x2="70" y2="103" stroke="currentColor" strokeWidth="3" />
            {/* Right chain connections */}
            <line x1="290" y1="183" x2="330" y2="103" stroke="currentColor" strokeWidth="3" />
            <line x1="370" y1="183" x2="330" y2="103" stroke="currentColor" strokeWidth="3" />
        </svg>
    );
}

export default function LawyerAnywherePage() {
    useEffect(() => {
        document.title = "Lawyer Anywhere — Early Access | Orion Intelligence";
        return () => {
            document.title = "Orion Intelligence";
        };
    }, []);



    const features = [
        { icon: Users, text: "Κατάλογος Δικηγόρων με φίλτρα ειδικότητας και περιοχής" },
        { icon: Calendar, text: "Online κράτηση ραντεβού με άμεση επιβεβαίωση" },
        { icon: MessageSquare, text: "Άμεσα μηνύματα με δικηγόρους πριν την κράτηση" },
        { icon: Star, text: "Αξιολογήσεις και κριτικές πελατών" },
        { icon: Globe, text: "Πλήρης υποστήριξη Ελληνικών και Αγγλικών" }
    ];

    return (
        <div
            className="min-h-screen flex flex-col relative overflow-hidden"
            style={{
                color: colors.text,
                background: `radial-gradient(ellipse at 20% 0%, rgba(139, 34, 82, 0.25), transparent 50%),
                             radial-gradient(ellipse at 80% 100%, rgba(201, 168, 76, 0.08), transparent 40%),
                             linear-gradient(170deg, ${colors.bgTop}, ${colors.bgBottom})`,
            }}
        >
            {/* Decorative Scales of Justice */}
            <ScalesOfJustice />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 mb-12"
                >
                    <img
                        src="/assets/orion-logo-800.png"
                        alt="Orion Intelligence logo"
                        className="w-10 h-10 rounded-xl"
                        style={{ boxShadow: `0 0 20px rgba(139, 34, 82, 0.4)` }}
                    />
                    <div className="text-left leading-tight">
                        <div className="text-lg font-bold tracking-wide" style={{ color: colors.primary }}>
                            Orion Intelligence
                        </div>
                        <div className="text-xs" style={{ color: colors.textMuted }}>Products • Lawyer Anywhere</div>
                    </div>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    {/* Left Column: Hero Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {/* Badge */}
                        <div
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
                            style={{
                                borderColor: colors.primary,
                                background: "rgba(201, 168, 76, 0.08)",
                                color: colors.primary
                            }}
                        >
                            <Scale size={16} />
                            <span className="text-xs font-bold uppercase tracking-widest">Legal Tech</span>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                            Lawyer Anywhere
                        </h1>

                        <p className="text-lg md:text-xl leading-relaxed font-medium mb-8" style={{ color: colors.textMuted }}>
                            Η σύγχρονη πλατφόρμα που συνδέει πολίτες με εξειδικευμένους δικηγόρους σε όλη την Ελλάδα.
                            <span className="block mt-2" style={{ color: colors.text }}>
                                Αυξήστε την πελατεία σας και οργανώστε τα ραντεβού σας ψηφιακά.
                            </span>
                        </p>

                        {/* Offer Box */}
                        <div
                            className="mb-10 p-6 rounded-2xl border"
                            style={{
                                background: "linear-gradient(135deg, rgba(139, 34, 82, 0.15), rgba(201, 168, 76, 0.05))",
                                borderColor: "rgba(201, 168, 76, 0.25)"
                            }}
                        >
                            <h3 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                                <Star className="fill-current" size={20} />
                                Προσφορά Γνωριμίας
                            </h3>
                            <p className="text-sm md:text-base" style={{ color: colors.textMuted }}>
                                Οι πρώτοι <strong style={{ color: colors.text }}>100 δικηγόροι</strong> που θα εγγραφούν κερδίζουν{" "}
                                <span className="font-bold underline underline-offset-2" style={{ color: colors.primary, textDecorationColor: colors.primary }}>
                                    ΔΩΡΕΑΝ συνδρομή
                                </span>{" "}
                                για τον <strong style={{ color: colors.text }}>1ο χρόνο</strong> λειτουργίας!
                            </p>
                        </div>

                        {/* App Screenshot Preview */}
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl group" style={{ border: `1px solid rgba(201, 168, 76, 0.15)` }}>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
                            <img
                                src="/assets/lawyeranywhere.png"
                                alt="App Preview"
                                className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute bottom-4 left-4 z-20">
                                <span className="text-xs font-medium px-2 py-1 rounded" style={{ background: "rgba(26, 10, 18, 0.8)", color: colors.textMuted, border: `1px solid rgba(201, 168, 76, 0.2)` }}>
                                    App Preview
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right Column: Sign Up Form */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div
                            className="p-8 rounded-3xl border backdrop-blur-xl relative overflow-hidden shadow-2xl"
                            style={{
                                background: "rgba(26, 10, 18, 0.6)",
                                borderColor: colors.cardBorder
                            }}
                        >
                            {/* Decorative Scale icon in form background */}
                            <Scale
                                size={120}
                                className="absolute -right-4 -top-4 opacity-[0.04] pointer-events-none"
                                style={{ color: colors.primary }}
                            />

                            <h2 className="text-2xl font-bold mb-2 relative z-10" style={{ color: colors.text }}>
                                Ξεκινήστε Τώρα
                            </h2>
                            <p className="text-sm mb-8 relative z-10" style={{ color: colors.textMuted }}>
                                Κατοχυρώστε τη θέση σας στους πρώτους 100 και κερδίστε 1 χρόνο ΔΩΡΕΑΝ.
                            </p>

                            <div className="space-y-4 relative z-10">
                                {/* Primary CTA - Lawyer */}
                                <a
                                    href="https://lawyers.orionintelligence.net/el/auth/register/lawyer"
                                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold transition-all shadow-lg"
                                    style={{
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryHover})`,
                                        color: colors.bgTop,
                                        boxShadow: "0 10px 30px -10px rgba(201, 168, 76, 0.4)"
                                    }}
                                >
                                    Εγγραφή ως Δικηγόρος <ArrowRight size={18} />
                                </a>

                                {/* Secondary CTA - Client */}
                                <a
                                    href="https://lawyers.orionintelligence.net/el/lawyers"
                                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold border transition-all"
                                    style={{
                                        borderColor: "rgba(201, 168, 76, 0.3)",
                                        color: colors.primary,
                                        background: "rgba(201, 168, 76, 0.05)"
                                    }}
                                >
                                    Αναζήτηση Δικηγόρου <ArrowRight size={16} />
                                </a>

                                {/* Login link */}
                                <p className="text-center text-sm" style={{ color: colors.textMuted }}>
                                    Έχετε ήδη λογαριασμό;{" "}
                                    <a
                                        href="https://lawyers.orionintelligence.net/el/auth/login"
                                        className="underline hover:opacity-80 transition-opacity"
                                        style={{ color: colors.primary }}
                                    >
                                        Σύνδεση
                                    </a>
                                </p>

                                <p className="text-[10px] text-center mt-2" style={{ color: colors.textMuted, opacity: 0.5 }}>
                                    Χωρίς πιστωτική κάρτα. Οι πρώτοι 100 δικηγόροι κερδίζουν 1 χρόνο ΔΩΡΕΑΝ.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Features Grid */}
                <div className="mt-20 pt-12" style={{ borderTop: `1px solid rgba(201, 168, 76, 0.1)` }}>
                    <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: colors.text }}>
                        Γιατί να μπείτε στο Lawyer Anywhere;
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-4 p-5 rounded-2xl border transition-colors group"
                                style={{
                                    background: "rgba(139, 34, 82, 0.05)",
                                    borderColor: "rgba(201, 168, 76, 0.1)"
                                }}
                            >
                                <div className="p-2 rounded-xl shrink-0" style={{ background: "rgba(201, 168, 76, 0.1)" }}>
                                    <feature.icon size={22} style={{ color: colors.primary }} />
                                </div>
                                <span className="text-sm font-medium leading-relaxed" style={{ color: colors.textMuted }}>{feature.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-12 text-center">
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 text-sm transition-colors"
                        style={{ color: colors.textMuted }}
                    >
                        ← Επιστροφή στην αρχική
                    </a>
                </div>

            </div>
        </div>
    );
}
